/**
 * AlertRuleDetailPanel.tsx
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Slide-out panel (drawer) showing alert rule details.
 *
 * Spec 197 (presentation-only): conformed to the portal modal/drawer standard —
 * transparent-but-blocking backdrop (page stays visible; outside-click no longer
 * dismisses), framed surface with a shadow, and design tokens in place of raw
 * gray/hex utilities. Behavior, data, state, and props are unchanged.
 */

import React from 'react';
import { X, Edit, Clock, Calendar, Bell, BellOff, Mail, MessageSquare, Webhook } from 'lucide-react';
import type { AlertRuleView, NotificationChannel, Severity } from '@shared/types/alertRuleManagement';
import { AssignmentPanel } from './AssignmentPanel';
import { formatEastern } from '@/utils/dateFormatter';

// =============================================================================
// Props
// =============================================================================

interface AlertRuleDetailPanelProps {
  rule: AlertRuleView | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (rule: AlertRuleView) => void;
}

// =============================================================================
// Helper Components
// =============================================================================

const SeverityBadge: React.FC<{ severity: Severity | null }> = ({ severity }) => {
  if (!severity) return <span className="text-[var(--color-text-secondary)]">Default</span>;

  const colors: Record<Severity, string> = {
    Critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    Warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    Info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    High: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    Medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    Low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[severity]}`}>
      {severity}
    </span>
  );
};

const ChannelIcon: React.FC<{ type: NotificationChannel['type'] }> = ({ type }) => {
  switch (type) {
    case 'Email':
      return <Mail className="w-4 h-4" />;
    case 'Teams':
      return <MessageSquare className="w-4 h-4" />;
    case 'Webhook':
      return <Webhook className="w-4 h-4" />;
  }
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-3 border-b border-[var(--color-border)] last:border-0">
    <dt className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</dt>
    <dd className="mt-1 text-sm text-[var(--color-text-primary)]">{children}</dd>
  </div>
);

// =============================================================================
// Component
// =============================================================================

export const AlertRuleDetailPanel: React.FC<AlertRuleDetailPanelProps> = ({
  rule,
  isOpen,
  onClose,
  onEdit,
}) => {
  if (!isOpen || !rule) return null;

  const formatDate = (date: Date) => {
    return formatEastern(date);
  };

  return (
    <>
      {/* Transparent backdrop: blocks input but keeps the page visible. Clicking
          it does NOT close — that is the portal-wide rule (Spec 197). */}
      <div
        className="fixed inset-0 bg-transparent z-40"
        aria-hidden="true"
      />

      {/* Panel (drawer) — framed + shadowed surface on tokenized card background. */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-[var(--color-bg-card)] text-[var(--color-text-primary)] border-l border-[var(--color-border)] shadow-2xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--color-bg-card)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {rule.enabled ? (
              <Bell className="w-5 h-5 text-[var(--color-status-success)]" />
            ) : (
              <BellOff className="w-5 h-5 text-[var(--color-text-secondary)]" />
            )}
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
              {rule.name}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(rule)}
              className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] rounded-md hover:bg-[var(--color-bg-subtle)]"
              title="Edit rule"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-md hover:bg-[var(--color-bg-subtle)]"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Status Badge */}
          <div className="mb-6">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                rule.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
              }`}
            >
              {rule.enabled ? (
                <>
                  <Bell className="w-4 h-4 mr-1" />
                  Enabled
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-1" />
                  Disabled
                </>
              )}
            </span>
          </div>

          {/* Basic Details */}
          <dl className="space-y-0">
            <DetailRow label="Alert Type">
              {rule.alertType.replace(/([A-Z])/g, ' $1').trim()}
            </DetailRow>

            <DetailRow label="Severity">
              <SeverityBadge severity={rule.severityOverride} />
            </DetailRow>

            <DetailRow label="Targeting">
              {/* Spec 192: junction-backed assignment management (FR-010) */}
              <AssignmentPanel ruleId={rule.id} />
            </DetailRow>

            <DetailRow label="Cooldown">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-[var(--color-text-secondary)]" />
                {rule.cooldownMinutes} minutes
              </div>
            </DetailRow>
          </dl>

          {/* Condition JSON */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
              Condition
            </h3>
            <div className="bg-[var(--color-bg-subtle)] rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
                {JSON.stringify(rule.conditionJson, null, 2)}
              </pre>
            </div>
          </div>

          {/* Notification Channels */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
              Notification Channels
            </h3>
            {rule.notificationChannels && rule.notificationChannels.length > 0 ? (
              <ul className="space-y-2">
                {rule.notificationChannels.map((channel, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 p-3 bg-[var(--color-bg-subtle)] rounded-lg"
                  >
                    <div className="flex-shrink-0 text-[var(--color-text-secondary)]">
                      <ChannelIcon type={channel.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[var(--color-text-primary)]">
                        {channel.type}
                      </div>
                      <div className="text-sm text-[var(--color-text-secondary)] truncate">
                        {channel.destination}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)] italic">
                No notification channels configured
              </p>
            )}
          </div>

          {/* Timestamps */}
          <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-xs font-medium text-[var(--color-text-secondary)] flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Created
                </dt>
                <dd className="mt-1 text-sm text-[var(--color-text-primary)]">
                  {formatDate(rule.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-[var(--color-text-secondary)] flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Updated
                </dt>
                <dd className="mt-1 text-sm text-[var(--color-text-primary)]">
                  {formatDate(rule.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-[var(--color-bg-card)] border-t border-[var(--color-border)] px-6 py-4">
          <div className="flex gap-3">
            <button
              onClick={() => onEdit(rule)}
              className="flex-1 px-4 py-2 bg-[var(--color-accent)] text-white rounded-md hover:bg-[var(--color-accent-hover)]"
            >
              Edit Rule
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-md hover:bg-[var(--color-bg-subtle)]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertRuleDetailPanel;
